<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    channelType: { type: String, default: '' },
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
    enableInteractive: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'setReplyMode',
    'toggleEditorSize',
    'executeCopilotAction',
    'collapseComposer',
  ],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };

    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();
    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      handleReplyClick,
      handleNoteClick,
      setReplyMode,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  computed: {
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      return this.mode === REPLY_EDITOR_MODES.REPLY;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },

    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
  methods: {
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
  },
};
</script>

<template>
  <div
    class="flex min-h-12 flex-wrap items-center justify-between gap-2 bg-transparent px-3 py-1.5"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      :enable-interactive="enableInteractive"
      @set-mode="setReplyMode"
    />
    <div v-if="isMessageLengthReachingThreshold" class="flex items-center">
      <div class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div class="ml-auto flex shrink-0 items-center gap-2">
      <div v-if="captainTasksEnabled" class="relative">
        <NextButton
          ref="copilotToggleRef"
          v-tooltip.top-end="
            $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.MENU_TOOLTIP')
          "
          :aria-label="
            $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.MENU_TOOLTIP')
          "
          :aria-expanded="showCopilotMenu"
          ghost
          :disabled="disabled || isEditorDisabled"
          :class="{
            'text-n-blue-11 hover:enabled:!bg-n-blue-3': !showCopilotMenu,
            'text-n-blue-11 bg-n-blue-3': showCopilotMenu,
          }"
          sm
          icon="i-woot-captain"
          @click="toggleCopilotMenu"
        />
        <CopilotMenuBar
          v-if="showCopilotMenu"
          v-on-click-outside="[
            handleClickOutside,
            { ignore: [copilotToggleRef] },
          ]"
          :has-selection="false"
          :has-content="hasContent"
          :conversation-id="conversationId"
          class="ltr:right-0 rtl:left-0 bottom-full mb-2"
          @execute-copilot-action="handleCopilotAction"
        />
      </div>
      <NextButton
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        :aria-label="signatureToggleTooltip"
        :aria-pressed="sendWithSignature"
        :disabled="disabled"
        icon="i-ph-signature"
        :label="
          $t(
            sendWithSignature
              ? 'CONVERSATION.FOOTER.SIGNATURE_ON'
              : 'CONVERSATION.FOOTER.SIGNATURE_OFF'
          )
        "
        :variant="sendWithSignature ? 'faded' : 'ghost'"
        :color="sendWithSignature ? 'blue' : 'slate'"
        type="button"
        class="rounded-lg text-xs"
        sm
        @click="toggleMessageSignature"
      />
      <NextButton
        v-if="captainTasksEnabled"
        ghost
        class="text-n-slate-11"
        sm
        icon="i-lucide-maximize-2"
        :aria-label="$t('CONVERSATION.REPLYBOX.COMPOSER_EXPAND')"
        :title="$t('CONVERSATION.REPLYBOX.COMPOSER_EXPAND')"
        @click="$emit('toggleEditorSize')"
      />
      <NextButton
        ghost
        class="hidden text-n-slate-11 md:inline-flex"
        sm
        icon="i-lucide-chevron-down"
        :aria-label="$t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSE')"
        :title="$t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSE')"
        @click="$emit('collapseComposer')"
      />
    </div>
  </div>
</template>
